import { Box, FormHelperText, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import _ from 'lodash';

import { Action, Actions, actionTypeLabel, FLOOR_DATA_LAG_MINUTES, formatMinutes } from '../types';

// actions that take a link or the device away from the customer
const DRASTIC_ACTIONS: string[] = [Actions.ActionResetDevice, Actions.ActionDisableInterface];

const SEVERITY_COLORS: { [type: string]: string } = {
    [Actions.ActionNotification]: '#D9AD00',
    [Actions.ActionResetDevice]: '#EE3939',
    [Actions.ActionDisableInterface]: '#EE3939',
};
const DEFAULT_COLOR = '#F39C12';

// minutes from traffic stopping to the action firing, data lag included
export const actsAfterMinutes = (action: Action, windowMinutes: number): number =>
    FLOOR_DATA_LAG_MINUTES + (action.consecutive_windows ?? 1) * windowMinutes;

// On a link that stops completely every tier is breached at once, so tiers fire in order of
// duration: a drastic action shorter than every notification fires before anyone is told.
export const escalationWarnings = (actions: Action[], windowMinutes: number): string[] => {
    const notifications = actions.filter(a => a.type === Actions.ActionNotification);
    const drastic = actions.filter(a => DRASTIC_ACTIONS.includes(a.type));
    if (drastic.length === 0) return [];
    if (notifications.length === 0) {
        return drastic.map(a => `${actionTypeLabel(a.type)} has no Send Notification action configured, so nobody is told when it fires.`);
    }
    const firstNotice = _.minBy(notifications, a => actsAfterMinutes(a, windowMinutes)) as Action;
    const noticeAt = actsAfterMinutes(firstNotice, windowMinutes);
    return drastic
        .filter(a => actsAfterMinutes(a, windowMinutes) < noticeAt)
        .map(a => `${actionTypeLabel(a.type)} can fire before Send Notification: on a link that stops completely it acts after ~${formatMinutes(actsAfterMinutes(a, windowMinutes))}, the first notification after ~${formatMinutes(noticeAt)}.`);
}

interface EscalationPreviewProps {
    actions: Action[];
    windowMinutes: number;
}

export function EscalationPreview(props: EscalationPreviewProps) {
    const { windowMinutes } = props;
    const actions = props.actions.filter(a => !a.revert && a.type);
    if (actions.length === 0 || windowMinutes <= 0) return null;

    const warnings = escalationWarnings(actions, windowMinutes);
    const longest = Math.max(...actions.map(a => actsAfterMinutes(a, windowMinutes)));

    const options: Highcharts.Options = {
        chart: { type: 'scatter', height: 220, backgroundColor: 'transparent', spacing: [12, 16, 8, 8] },
        title: { text: undefined },
        credits: { enabled: false },
        legend: { enabled: false },
        exporting: { enabled: false },
        xAxis: {
            min: 0,
            max: Math.ceil(longest * 1.25 / 10) * 10,
            title: { text: 'Acts after traffic drops (minutes, incl. data delay)', style: { fontSize: '10px' } },
            gridLineWidth: 1,
            gridLineColor: '#EEF0F3',
            labels: { style: { fontSize: '10px', color: '#777' } },
        },
        yAxis: {
            min: 0,
            max: 100,
            title: { text: 'At or below % of minimum', style: { fontSize: '10px' } },
            gridLineColor: '#EEF0F3',
            labels: { format: '{value}%', style: { fontSize: '10px', color: '#777' } },
        },
        tooltip: {
            formatter: function () {
                const a = actions[this.point.index];
                return `<b>${actionTypeLabel(a.type)}</b><br/>≤${a.usage_percentage}% for ${formatMinutes((a.consecutive_windows ?? 1) * windowMinutes)}<br/>acts ~${formatMinutes(this.x as number)} after traffic drops`;
            },
        },
        plotOptions: {
            scatter: {
                animation: false,
                marker: { radius: 6, symbol: 'circle', lineColor: '#FFFFFF', lineWidth: 2 },
                dataLabels: {
                    enabled: true,
                    allowOverlap: false,
                    align: 'left',
                    x: 8,
                    y: -2,
                    style: { fontSize: '11px', fontWeight: '600', textOutline: 'none', color: '#222' },
                    formatter: function () { return actionTypeLabel(actions[this.point.index].type); },
                },
            },
        },
        series: [{
            type: 'scatter',
            name: 'Actions',
            data: actions.map(a => ({
                x: actsAfterMinutes(a, windowMinutes),
                y: a.usage_percentage,
                color: SEVERITY_COLORS[a.type] ?? DEFAULT_COLOR,
            })),
        }],
    };

    return (
        <Box className='floor_quota--preview'>
            <Typography className='floor_quota--panel--title'>Escalation preview</Typography>
            <HighchartsReact highcharts={Highcharts} options={options} />
            {warnings.map(w => <FormHelperText key={w} error>⚠ {w}</FormHelperText>)}
            <FormHelperText>Lower means further below the minimum; further right means a longer wait. Advisory only, it doesn't block saving.</FormHelperText>
        </Box>
    );
}
